import type { PortalNotification } from './notificationHref';

/** Session-only history. Viewed state drives the badge; acknowledged state tracks DELETE success. */
export type CachedNotification = PortalNotification & { viewed_at?: string | null; acknowledged_at?: string | null };

export const notificationCacheKey = (userId: number) => `portal-notification-session:${userId}`;
export const pendingAcknowledgementKey = (userId: number) => `portal-notification-ack-pending:${userId}`;

type SessionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Restore this user's bell history from sessionStorage; malformed data becomes an empty list. */
export function readNotificationCache(storage: SessionStorage, userId: number): CachedNotification[] {
  try {
    const items = JSON.parse(storage.getItem(notificationCacheKey(userId)) ?? '[]');
    return Array.isArray(items)
      ? items.filter((item): item is CachedNotification => item && typeof item.id === 'string')
      : [];
  } catch {
    return [];
  }
}

/** Persist bell history for reloads in the same browser tab. */
export function writeNotificationCache(storage: SessionStorage, userId: number, items: CachedNotification[]) {
  try { storage.setItem(notificationCacheKey(userId), JSON.stringify(items)); } catch { /* The in-memory cache remains usable. */ }
}

/** Remove bell history and failed DELETE retries when the user logs out or changes identity. */
export function clearNotificationSession(storage: SessionStorage, userId: number) {
  try {
    storage.removeItem(notificationCacheKey(userId));
    storage.removeItem(pendingAcknowledgementKey(userId));
  } catch { /* The backend still controls undelivered notifications. */ }
}

/** Add a delivered notification once, retaining its existing viewed and acknowledged state. */
export function addCachedNotification(items: CachedNotification[], notification: PortalNotification): CachedNotification[] {
  const previous = items.find(item => item.id === notification.id);
  return [{ ...notification, viewed_at: previous?.viewed_at ?? null, acknowledged_at: previous?.acknowledged_at ?? null }, ...items.filter(item => item.id !== notification.id)];
}

/** Mark an item viewed locally without removing it from the bell history. */
export function viewCachedNotification(items: CachedNotification[], id: string): CachedNotification[] {
  const viewedAt = new Date().toISOString();
  return items.map(item => item.id === id ? { ...item, viewed_at: item.viewed_at ?? viewedAt } : item);
}

/** Record a successful backend DELETE while retaining the item until logout. */
export function acknowledgeCachedNotification(items: CachedNotification[], id: string): CachedNotification[] {
  const acknowledgedAt = new Date().toISOString();
  return items.map(item => item.id === id ? { ...item, acknowledged_at: item.acknowledged_at ?? acknowledgedAt } : item);
}
